import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Award,
  BookOpen,
  Home,
  Menu,
  Trophy,
  User,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Lente, useArrasteQueTrava } from '@/components/ui/lente';

interface BarraInferiorProps {
  /** Com gamificação ligada, o terceiro destino é o ranking; sem ela, os certificados. */
  gamificacao: boolean;
  /** Abre ou fecha a gaveta com o menu completo (itens do admin, DevFlix, sair). */
  onAbrirMenu: () => void;
  /** Fecha a gaveta (ao escolher um destino da barra). */
  onFecharMenu: () => void;
  menuAberto: boolean;
}

interface Destino {
  titulo: string;
  url: string;
  Icone: LucideIcon;
  /** Outras rotas que acendem o mesmo destino (ex.: a página de um curso acende "Cursos"). */
  prefixo?: string;
}

/**
 * Barra fixa embaixo no celular, com os destinos que a pessoa usa toda semana.
 *
 * Antes, no celular, tudo passava pelo hambúrguer do topo: navegação escondida
 * corta a descoberta quase pela metade (NN/g) e fica fora do alcance do
 * polegar. O menu completo continua a um toque, no último item.
 *
 * Funciona como a lateral do computador (30/10: "o botão do menu lá embaixo no
 * mobile tem que funcionar do mesmo jeito"): a lente desliza até o destino
 * tocado, e com o dedo apertado dá para arrastá-la pela barra — ela vai junto,
 * trava em cada destino e, ao soltar, abre o destino onde travou.
 */
export function BarraInferior({
  gamificacao,
  onAbrirMenu,
  onFecharMenu,
  menuAberto,
}: BarraInferiorProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const quieto = useReducedMotion();

  const destinos: Destino[] = [
    { titulo: 'Início', url: '/home', Icone: Home },
    { titulo: 'Cursos', url: '/courses', Icone: BookOpen, prefixo: '/courses' },
    gamificacao
      ? { titulo: 'Ranking', url: '/ranking', Icone: Trophy }
      : { titulo: 'Certificados', url: '/certificates', Icone: Award },
    { titulo: 'Perfil', url: '/profile', Icone: User },
  ];
  const MENU = destinos.length;

  const daRota = destinos.findIndex(
    (d) =>
      pathname === d.url || (!!d.prefixo && pathname.startsWith(d.prefixo)),
  );

  const motor = useArrasteQueTrava<HTMLUListElement>({
    eixo: 'x',
    seletor: '[data-destino]',
    atual: menuAberto ? MENU : daRota,
    aoEscolher: (i) => {
      if (i === MENU) {
        if (!menuAberto) onAbrirMenu();
        return;
      }
      onFecharMenu();
      if (destinos[i]) navigate(destinos[i].url);
    },
  });

  const conteudo = (i: number, titulo: string, Icone: LucideIcon) => {
    const aceso = motor.lente === i;
    return (
      <>
        <motion.span
          className="flex h-6 items-center justify-center"
          initial={false}
          // ao acender, o ícone dá um pulinho (sobe, cresce e assenta)
          animate={
            aceso && !quieto
              ? { scale: [1, 1.24, 1], y: [0, -3, 0] }
              : { scale: 1, y: 0 }
          }
          transition={{ duration: 0.42, ease: [0.22, 1.2, 0.36, 1] }}
        >
          <Icone className="h-5 w-5" strokeWidth={aceso ? 2.4 : 2} />
        </motion.span>
        <span className="leading-none">{titulo}</span>
      </>
    );
  };

  const classeItem = (aceso: boolean) =>
    cn(
      'relative z-[1] mx-auto my-1.5 flex h-[3.25rem] w-full max-w-[4.75rem] flex-col items-center justify-center gap-1 rounded-md text-[11px] font-medium transition-colors duration-200',
      aceso ? 'text-primary' : 'text-muted-foreground',
    );

  return (
    <nav
      aria-label="Navegação móvel"
      className="barra-inferior border-border/60 bg-card/95 fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur-xl lg:hidden"
    >
      <ul
        ref={motor.ref}
        {...motor.props}
        className="relative mx-auto grid max-w-lg select-none grid-cols-5 px-1.5 [-webkit-touch-callout:none]"
      >
        <Lente
          caixa={motor.caixa}
          arrastando={motor.arrastando}
          apertada={motor.apertado}
          className="bg-primary/15 ring-primary/25 rounded-md ring-1 ring-inset"
        />
        {destinos.map((d, i) => (
          <li key={d.url} className="flex">
            <Link
              to={d.url}
              data-destino=""
              draggable={false}
              onClick={() => {
                motor.acender(i);
                onFecharMenu();
              }}
              aria-current={daRota === i && !menuAberto ? 'page' : undefined}
              className={classeItem(motor.lente === i)}
            >
              {conteudo(i, d.titulo, d.Icone)}
            </Link>
          </li>
        ))}
        <li className="flex">
          <button
            type="button"
            data-destino=""
            onClick={() => {
              motor.acender(MENU);
              onAbrirMenu();
            }}
            aria-expanded={menuAberto}
            className={classeItem(motor.lente === MENU)}
          >
            {conteudo(MENU, 'Menu', Menu)}
          </button>
        </li>
      </ul>
    </nav>
  );
}
